/**
 * Permission utility for role-based access control
 */

// Role IDs based on backend
export const ROLE_IDS = {
  READ: 2,
  WRITE: 1,
  SUPER_USER: 3,
  ADMIN: 4,
} as const;

// Role names
export const ROLE_NAMES = {
  READ: "Read",
  WRITE: "Write",
  SUPER_USER: "SuperUser",
  ADMIN: "Admin",
} as const;

// Permission levels for different features
export interface IPermissions {
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
}

/**
 * Get permissions for Region Data (Fuel Products) based on role ID
 * Admin: View, Add, Edit, Delete, Export
 * SuperUser: View, Add, Edit, Delete, Export
 * Write: View and Export only
 * Read: View and Export only
 */
export const getRegionDataPermissions = (roleId: number): IPermissions => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
    case ROLE_IDS.SUPER_USER:
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: true,
        canExport: true,
      };
    case ROLE_IDS.WRITE:
    case ROLE_IDS.READ:
      return {
        canView: true,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: true,
      };
    default:
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      };
  }
};

/**
 * Get permissions for User Management based on role ID
 * Admin: View, Add, Edit (all permissions)
 * SuperUser: View only
 * Write: View only
 * Read: No access
 */
export const getUserManagementPermissions = (roleId: number): IPermissions => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: false,
        canExport: false,
      };
    case ROLE_IDS.SUPER_USER:
    case ROLE_IDS.WRITE:
      return {
        canView: true,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      };
    case ROLE_IDS.READ:
    default:
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      };
  }
};

/**
 * Get permissions for Business Partners and Products Table based (since they have same permission) on role ID
 * Admin: View, Add, Import (all permissions)
 * SuperUser: View, Add, Import (all permissions)
 * Write: View and Export only
 * Read: View and Export only
 */
export const getBusinessPartnersPermissions = (
  roleId: number,
): IPermissions => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
    case ROLE_IDS.SUPER_USER:
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: false,
        canExport: true,
      };
    case ROLE_IDS.WRITE:
    case ROLE_IDS.READ:
      return {
        canView: true,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: true,
      };
    default:
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      };
  }
};

export const getVatRatesPermissions = (roleId: number): IPermissions => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
    case ROLE_IDS.SUPER_USER:
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: false,
        canExport: true,
      };
    case ROLE_IDS.WRITE:
      return {
        canView: true,
        canAdd: true,
        canEdit: false,
        canDelete: false,
        canExport: true,
      };
    case ROLE_IDS.READ:
      return {
        canView: true,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: true,
      };
    default:
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      };
  }
};
/**
 * Get permissions for GSAP Volumes based on role ID
 * Admin: View, Import (all permissions)
 * SuperUser: View, Import (all permissions)
 * Write: View, Import (all permissions)
 * Read: View and Export only
 */
export const getGSAPVolumesPermissions = (roleId: number): IPermissions => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
    case ROLE_IDS.SUPER_USER:
    case ROLE_IDS.WRITE:
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: false,
        canExport: true,
      };
    case ROLE_IDS.READ:
      return {
        canView: true,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: true,
      };
    default:
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      };
  }
};

/**
 * Extended permissions interface for Auxiliary Site Info
 * Includes additional permissions for mapping functionality
 */
export interface IAuxiliarySiteInfoPermissions extends IPermissions {
  canMapUnmap: boolean;
  canViewMapped: boolean;
}

/**
 * Get permissions for Auxiliary Site Info based on role ID
 * Admin: All permissions (view table, create placeholder site, map/unmap menu, view menu)
 * SuperUser: All permissions (view table, create placeholder site, map/unmap menu, view menu)
 * Write: View table, View menu, Map/Unmap menu, Edit (and Export)
 * Read: View table, View menu only
 */
export const getAuxiliarySiteInfoPermissions = (
  roleId: number,
): IAuxiliarySiteInfoPermissions => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
    case ROLE_IDS.SUPER_USER:
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: false,
        canExport: true,
        canMapUnmap: true,
        canViewMapped: true,
      };
    case ROLE_IDS.WRITE:
      return {
        canView: true,
        canAdd: false,
        canEdit: true,
        canDelete: false,
        canExport: true,
        canMapUnmap: true,
        canViewMapped: true,
      };
    case ROLE_IDS.READ:
      return {
        canView: true,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: true,
        canMapUnmap: false,
        canViewMapped: true,
      };
    default:
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
        canMapUnmap: false,
        canViewMapped: false,
      };
  }
};

/**
 * Extended permissions interface for Plant Details
 * Includes additional permissions for mapping functionality
 */
export interface IPlantDetailsPermissions extends IPermissions {
  canMapUnmap: boolean;
  canViewMapped: boolean;
}

/**
 * Get permissions for Plant Details based on role ID
 * Admin: All permissions (view table, add, export, edit row, view mapped sites, map/unmap sites)
 * SuperUser: All permissions (view table, add, export, edit row, view mapped sites, map/unmap sites)
 * Write: View table, Export, Edit row, View Mapped sites menu, Map/Unmap sites menu
 * Read: View table, Export, View Mapped sites menu only
 */
export const getPlantDetailsPermissions = (
  roleId: number,
): IPlantDetailsPermissions => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
    case ROLE_IDS.SUPER_USER:
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: false,
        canExport: true,
        canMapUnmap: true,
        canViewMapped: true,
      };
    case ROLE_IDS.WRITE:
      return {
        canView: true,
        canAdd: false,
        canEdit: true,
        canDelete: false,
        canExport: true,
        canMapUnmap: true,
        canViewMapped: true,
      };
    case ROLE_IDS.READ:
      return {
        canView: true,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: true,
        canMapUnmap: false,
        canViewMapped: true,
      };
    default:
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
        canMapUnmap: false,
        canViewMapped: false,
      };
  }
};

/**
 * Get permissions for OMR Price Quotes based on role ID
 * Admin: Full access to all tabs (OMR Fuel Prices, AdBlue and LPG, Import)
 * SuperUser: Full access to all tabs
 * Write: Full access to all tabs
 * Read: OMR Fuel Prices only (no AdBlue/LPG, no Import)
 */
export const getOMRPricePermissions = (roleId: number): IPermissions => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
    case ROLE_IDS.SUPER_USER:
    case ROLE_IDS.WRITE:
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: false,
        canExport: true,
      };
    case ROLE_IDS.READ:
      return {
        canView: true,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: true,
      };
    default:
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      };
  }
};

/**
 * Get permissions for Generate Report based on role ID
 * Admin: Can access Generate Reports
 * SuperUser: Can access Generate Reports
 * Write: Can access Generate Reports
 * Read: No access
 */
export const getGenerateReportPermissions = (roleId: number): IPermissions => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
    case ROLE_IDS.SUPER_USER:
    case ROLE_IDS.WRITE:
      return {
        canView: true,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      };
    case ROLE_IDS.READ:
    default:
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      };
  }
};

/**
 * Get permissions for Contract Parameters based on role ID
 * Admin: Create, Edit, View, Export (all permissions for Partner Level, Site Level, and Product Level contracts)
 * SuperUser: Create, Edit, View, Export (all permissions for Partner Level, Site Level, and Product Level contracts)
 * Write: View and Export only (for Partner Level, Site Level, and Product Level contracts)
 * Read: View and Export only (for Partner Level, Site Level, and Product Level contracts)
 * Note: Import Contract and Import Logs functionality follows the same permissions as above
 */
export const getContractParametersPermissions = (
  roleId: number,
): IPermissions => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
    case ROLE_IDS.SUPER_USER:
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: false,
        canExport: true,
      };
    case ROLE_IDS.WRITE:
    case ROLE_IDS.READ:
      return {
        canView: true,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: true,
      };
    default:
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      };
  }
};

/**
 * Check if user has specific permission
 */
export const hasPermission = (
  roleId: number,
  feature:
    | "regionData"
    | "userManagement"
    | "businessPartners"
    | "gsapVolumes"
    | "omrPrice"
    | "generateReport"
    | "vatRates"
    | "contractParameters",
  action: keyof IPermissions,
): boolean => {
  let permissions: IPermissions;

  switch (feature) {
    case "regionData":
      permissions = getRegionDataPermissions(roleId);
      break;
    case "userManagement":
      permissions = getUserManagementPermissions(roleId);
      break;
    case "businessPartners":
      permissions = getBusinessPartnersPermissions(roleId);
      break;
    case "gsapVolumes":
      permissions = getGSAPVolumesPermissions(roleId);
      break;
    case "omrPrice":
      permissions = getOMRPricePermissions(roleId);
      break;
    case "generateReport":
      permissions = getGenerateReportPermissions(roleId);
      break;
    case "vatRates":
      permissions = getVatRatesPermissions(roleId);
      break;
    case "contractParameters":
      permissions = getContractParametersPermissions(roleId);
      break;
    default:
      permissions = {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      };
  }

  return permissions[action];
};

/**
 * Permissions interface for PlusCard-specific features
 * Controls access to sub-features within the PlusCard section
 */
export interface IPlusCardPermissions {
  canViewListOfPlusCards: boolean;
  canViewCardsPerPartner: boolean;
  canSendActiveCardsToTandem: boolean;
  canViewPlusCardHistory: boolean;
  canImportCards: boolean;
  canViewTransactions: boolean;
}

/**
 * Get permissions for PlusCard features based on role ID
 * Admin:     All features
 * SuperUser: All features
 * Write:     All features except Send active cards to Tandem
 * Read:      View/Download only (no Import, no Send to Tandem)
 */
export const getPlusCardPermissions = (
  roleId: number,
): IPlusCardPermissions => {
  switch (roleId) {
    case ROLE_IDS.ADMIN:
    case ROLE_IDS.SUPER_USER:
      return {
        canViewListOfPlusCards: true,
        canViewCardsPerPartner: true,
        canSendActiveCardsToTandem: true,
        canViewPlusCardHistory: true,
        canImportCards: true,
        canViewTransactions: true,
      };
    case ROLE_IDS.WRITE:
      return {
        canViewListOfPlusCards: true,
        canViewCardsPerPartner: true,
        canSendActiveCardsToTandem: false,
        canViewPlusCardHistory: true,
        canImportCards: true,
        canViewTransactions: true,
      };
    case ROLE_IDS.READ:
      return {
        canViewListOfPlusCards: true,
        canViewCardsPerPartner: true,
        canSendActiveCardsToTandem: false,
        canViewPlusCardHistory: true,
        canImportCards: false,
        canViewTransactions: true,
      };
    default:
      return {
        canViewListOfPlusCards: false,
        canViewCardsPerPartner: false,
        canSendActiveCardsToTandem: false,
        canViewPlusCardHistory: false,
        canImportCards: false,
        canViewTransactions: false,
      };
  }
};
